import { oc } from "@orpc/contract";
import { z } from "zod";

const UUIDSchema = z.uuid();

/** How confidently we resolved the supplier from an email's `from` address. */
export const SupplierConfidenceSchema = z.enum(["resolved", "unknown"]);

export const DetectedSupplierSchema = z.object({
  supplierId: UUIDSchema.nullable(),
  supplierName: z.string().nullable(),
  confidence: SupplierConfidenceSchema,
  hasParseConfig: z.boolean(),
});

/** One named contribution to the is-an-849 score, so the UI can explain it. */
export const Email849ScoreSignalSchema = z.object({
  key: z.enum([
    "sender-unresolved",
    "sender-resolved",
    "parser-configured",
    "has-attachments",
    "internal-subject",
    "existing-849",
    "sender-labeled-849",
    "sender-labeled-not-849",
  ]),
  delta: z.number(),
});

export const Manual849ListItemSchema = z.object({
  id: UUIDSchema,
  from: z.string(),
  to: z.string(),
  subject: z.string().nullable(),
  attachmentCount: z.number().nullable(),
  receivedAt: z.coerce.date().nullable(),
  createdAt: z.coerce.date(),
  detectedSupplier: DetectedSupplierSchema,
  existingResponseId: UUIDSchema.nullable(),
  /** Cheap read-only triage scores (0–1): is this an 849, and does it relate to one.
   *  `signals` are the named contributions that produced `isAn849` — rendered as the
   *  "why this score" breakdown. */
  confidence: z.object({
    isAn849: z.number().min(0).max(1),
    relatedToExisting: z.number().min(0).max(1),
    signals: z.array(Email849ScoreSignalSchema),
  }),
  /** Email-thread context: how many mailbox emails share this conversation, and
   *  whether the thread already has an 849. size>1 && !hasResponse ⇒ likely a thread
   *  whose original 849 was missed. */
  thread: z.object({
    size: z.int().positive(),
    hasResponse: z.boolean(),
  }),
});

export const Manual849PageInfoSchema = z.object({
  currentPage: z.int().positive(),
  totalPages: z.int().nonnegative(),
  totalCount: z.int().nonnegative(),
  hasNextPage: z.boolean(),
  hasPreviousPage: z.boolean(),
});

export const ListManual849InputSchema = z.object({
  page: z.int().min(1).optional(),
  pageSize: z.int().min(1).max(100).optional(),
  search: z.string().optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  subject: z.string().optional(),
  onlyWithoutResponse: z.boolean().optional(),
  /** Hide emails sent from internal `rexel.ca` addresses (they're not supplier 849s). */
  hideInternalSenders: z.boolean().optional(),
  /** Fetch a single email by id (used to open the viewer for a deep link that isn't
   *  on the current page). */
  inboundEmailId: UUIDSchema.optional(),
});

export const ListManual849OutputSchema = z.object({
  edges: z.array(Manual849ListItemSchema),
  pageInfo: Manual849PageInfoSchema,
});

export const DetectSupplierInputSchema = z.object({
  inboundEmailId: UUIDSchema,
});

// ── Reconcile 849 (the engine) ──────────────────────────────────────────────

/** Keep in sync with `EDI849ResponseStatusSchema` and the `SD849ResponseStatus` model enum. */
export const SD849ResponseStatusSchema = z.enum([
  "accepted",
  "rejected",
  "partial",
  "unknown",
  "resubmitted",
  "advisory",
]);

/** An existing 849 response an email may belong to (surfaced for confirmation). */
export const AttachCandidateSchema = z.object({
  responseId: UUIDSchema,
  supplierName: z.string(),
  status: SD849ResponseStatusSchema,
  overlappingLineCount: z.int().nonnegative(),
  reason: z.enum(["shared-844-lines", "same-supplier-recent", "same-adm"]),
  createdAt: z.coerce.date(),
});

/** The engine's "here's what I was unsure about" channel. */
export const OpenQuestionSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("supplier"), reason: z.string() }),
  z.object({
    kind: z.literal("attach-candidate"),
    candidates: z.array(AttachCandidateSchema),
  }),
  z.object({
    kind: z.literal("poor-parse"),
    supplierId: z.string(),
    supplierName: z.string(),
  }),
]);

export const ReconcileOutcomeSchema = z.enum([
  "created",
  "attached",
  "evolved",
  "already-exists",
  "dismissed",
  "needs-input",
]);

export const ReconciliationSchema = z.object({
  outcome: ReconcileOutcomeSchema,
  responseId: UUIDSchema.nullable(),
  supplier: DetectedSupplierSchema,
  parse: z.object({
    status: SD849ResponseStatusSchema.nullable(),
    acceptedCount: z.number(),
    rejectedCount: z.number(),
    partialCount: z.number(),
    lineCount: z.number(),
  }),
  match: z.object({ ran: z.boolean(), note: z.string() }),
  openQuestions: z.array(OpenQuestionSchema),
  summary: z.string(),
});

export const ReconcileInputSchema = z.object({
  inboundEmailId: UUIDSchema,
  /** An expert-confirmed S&D *company* id; overrides sender detection. */
  supplierCompanyId: UUIDSchema.optional(),
  /** "force-create": create a new 849 even when attach candidates exist. */
  resolveAs: z.enum(["auto", "force-create"]).optional(),
  /** "thread": reconcile the whole conversation into one 849 (anchor on the original). */
  scope: z.enum(["email", "thread"]).optional(),
  /** Thread scope: the email to create the 849 from (defaults to the earliest). */
  anchorEmailId: UUIDSchema.optional(),
  /** Thread scope: which thread emails to fold in (defaults to all). */
  includeEmailIds: z.array(UUIDSchema).optional(),
});

export const ThreadEmailSchema = z.object({
  id: UUIDSchema,
  subject: z.string().nullable(),
  from: z.string(),
  receivedAt: z.coerce.date(),
  hasAttachment: z.boolean(),
  existingResponseId: UUIDSchema.nullable(),
});

export const ThreadPreviewSchema = z.object({
  emails: z.array(ThreadEmailSchema),
  suggestedAnchorId: UUIDSchema.nullable(),
  foundOriginal: z.boolean(),
});

/** The expert's authoritative correction of one reconcile decision. */
export const CorrectionSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("supplier"), companyId: UUIDSchema }),
  z.object({ kind: z.literal("attach"), targetResponseId: UUIDSchema }),
  /** Link the email to an existing 849 as CONTEXT only — no parsing, no line
   *  decisions. For thread replies / FYIs that belong to a claim but aren't the
   *  supplier's decision report. */
  z.object({ kind: z.literal("attach-context"), targetResponseId: UUIDSchema }),
  /** With `inboundEmailId`: dismiss the email. With `responseId`: delete the
   *  wrongly-created 849 (refused once decisions were applied to 844 lines) and
   *  dismiss every email linked to it. */
  z.object({ kind: z.literal("not-an-849") }),
  z.object({
    kind: z.literal("line-match"),
    lineRef: z.object({
      responseId: UUIDSchema,
      lineIndex: z.int().nonnegative(),
    }),
    sdE844dId: UUIDSchema,
  }),
]);

export const CorrectInputSchema = z.object({
  /** The email being reconciled, or a response id for a line-level correction. */
  inboundEmailId: UUIDSchema.optional(),
  responseId: UUIDSchema.optional(),
  correction: CorrectionSchema,
});

const listContract = oc
  .input(ListManual849InputSchema)
  .output(ListManual849OutputSchema)
  .route({
    method: "GET",
    summary:
      "List inbound emails as 849-response candidates (admin marking inbox)",
  });

const detectSupplierContract = oc
  .input(DetectSupplierInputSchema)
  .output(DetectedSupplierSchema)
  .route({
    method: "GET",
    summary: "Detect the S&D supplier for an inbound email from its sender",
  });

const suggestCandidatesContract = oc
  .input(z.object({ inboundEmailId: UUIDSchema }))
  .output(z.array(AttachCandidateSchema))
  .route({
    method: "GET",
    summary:
      "Cheap read-only 849 responses this email may belong to (surface on open)",
  });

const threadContract = oc
  .input(z.object({ inboundEmailId: UUIDSchema }))
  .output(ThreadPreviewSchema)
  .route({
    method: "GET",
    summary:
      "Preview an email's thread for the reconcile-thread confirmation panel",
  });

const reconcileContract = oc
  .input(ReconcileInputSchema)
  .output(ReconciliationSchema)
  .route({
    method: "POST",
    summary:
      "Reconcile an inbound email into the 849 space (create / attach / evolve / suggest)",
  });

const correctContract = oc
  .input(CorrectInputSchema)
  .output(ReconciliationSchema)
  .route({
    method: "POST",
    summary:
      "Apply the expert's authoritative correction to a reconcile decision",
  });

export const manual849Contracts = {
  list: listContract,
  detectSupplier: detectSupplierContract,
  suggestCandidates: suggestCandidatesContract,
  thread: threadContract,
  reconcile: reconcileContract,
  correct: correctContract,
};

export type SupplierConfidence = z.infer<typeof SupplierConfidenceSchema>;
export type DetectedSupplier = z.infer<typeof DetectedSupplierSchema>;
export type Manual849ListItem = z.infer<typeof Manual849ListItemSchema>;
export type ListManual849Input = z.infer<typeof ListManual849InputSchema>;
export type ListManual849Output = z.infer<typeof ListManual849OutputSchema>;
export type AttachCandidate = z.infer<typeof AttachCandidateSchema>;
export type OpenQuestion = z.infer<typeof OpenQuestionSchema>;
export type Reconciliation = z.infer<typeof ReconciliationSchema>;
export type ReconcileInput = z.infer<typeof ReconcileInputSchema>;
export type ThreadEmail = z.infer<typeof ThreadEmailSchema>;
export type ThreadPreview = z.infer<typeof ThreadPreviewSchema>;
export type Correction = z.infer<typeof CorrectionSchema>;
export type CorrectInput = z.infer<typeof CorrectInputSchema>;
